(() => {
  if (window.CLPConfigurationPickerLoaded) return;
  window.CLPConfigurationPickerLoaded = true;

  const FILTERS = [
    { key: 'clpResolution', label: 'Resolution' },
    { key: 'clpPrintMethod', label: 'Print method' },
    { key: 'clpConnectivity', label: 'Connection' },
    { key: 'clpMedia', label: 'Media handling' },
  ];

  const clean = (value) => (value || '').trim();

  function createFilter(root, items, config) {
    const values = [...new Set(items.map((item) => clean(item.dataset[config.key])).filter(Boolean))];
    if (values.length < 2) return null;

    const label = document.createElement('label');
    label.className = 'clp-config-filter';

    const visibleLabel = document.createElement('span');
    visibleLabel.textContent = config.label;

    const select = document.createElement('select');
    select.dataset.clpFilterKey = config.key;
    select.setAttribute('aria-label', `Filter configurations by ${config.label.toLowerCase()}`);

    const allOption = document.createElement('option');
    allOption.value = '';
    allOption.textContent = `All ${config.label.toLowerCase()}`;
    select.append(allOption);

    values.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.append(option);
    });

    label.append(visibleLabel, select);
    return label;
  }

  function buildConfigurationSummary(item) {
    const savedSummary = clean(item.dataset.clpConfigurationSummary);
    if (savedSummary) return savedSummary;

    const resolution = clean(item.dataset.clpResolution);
    const printMethod = clean(item.dataset.clpPrintMethod);
    const connectivity = clean(item.dataset.clpConnectivity);
    const media = clean(item.dataset.clpMedia);
    const methodLabel = printMethod === 'Direct Thermal Only'
      ? 'direct-thermal'
      : printMethod === 'Thermal Transfer'
        ? 'thermal-transfer'
        : printMethod.toLowerCase();
    const lead = [resolution, methodLabel, 'printer'].filter(Boolean).join(' ');
    let summary = lead;

    if (media) summary += ` configured for ${media.toLowerCase()} media handling`;
    if (connectivity) summary += ` with ${connectivity} connectivity`;
    summary += '.';

    if (printMethod === 'Direct Thermal Only') {
      summary += ' Prints on compatible direct-thermal labels without a ribbon.';
    } else if (printMethod === 'Thermal Transfer') {
      summary += ' Supports thermal-transfer printing with ribbon and direct-thermal printing without ribbon.';
    }

    return summary;
  }

  function updateCurrent(root, item) {
    if (!item) return;
    const title = root.querySelector('[data-clp-current-title]');
    const chips = root.querySelector('[data-clp-current-chips]');
    const summary = root.closest('variant-picker')?.querySelector('.clp-selection-summary');
    const rows = summary ? [...summary.querySelectorAll('.clp-selection-summary__grid > div')] : [];
    const rowByLabel = (label) => rows.find((row) => clean(row.querySelector('dt')?.textContent).toLowerCase() === label);
    const partNumber = summary?.querySelector('[data-clp-summary-part-number]') || rowByLabel('zebra part number')?.querySelector('dd');
    const configuration = summary?.querySelector('[data-clp-summary-configuration]') || rowByLabel('printer configuration')?.querySelector('dd');
    let descriptionContainer = summary?.querySelector('[data-clp-summary-description-container]') || summary?.querySelector('.clp-selection-summary__description');
    let description = descriptionContainer?.querySelector('[data-clp-summary-description]') || descriptionContainer?.querySelector('p');
    const stockBadge = summary?.querySelector('[data-clp-summary-stock]') || summary?.querySelector('.clp-stock-badge');

    if (!descriptionContainer && summary) {
      descriptionContainer = document.createElement('div');
      descriptionContainer.className = 'clp-selection-summary__description';
      descriptionContainer.dataset.clpSummaryDescriptionContainer = '';
      const heading = document.createElement('strong');
      heading.textContent = 'Configuration details';
      description = document.createElement('p');
      description.dataset.clpSummaryDescription = '';
      descriptionContainer.append(heading, description);
      summary.querySelector('.clp-selection-summary__notes')?.before(descriptionContainer);
    }

    if (title) title.textContent = clean(item.dataset.clpResolution) || 'Selected configuration';
    if (chips) {
      chips.replaceChildren();
      ['clpPrintMethod', 'clpConnectivity', 'clpMedia'].forEach((key) => {
        const value = clean(item.dataset[key]);
        if (!value) return;
        const chip = document.createElement('span');
        chip.textContent = value;
        chips.append(chip);
      });
    }

    if (partNumber) partNumber.textContent = clean(item.dataset.clpPartNumber);
    if (configuration) configuration.textContent = item.querySelector('input[type="radio"]')?.value || '';

    const configurationSummary = buildConfigurationSummary(item);
    if (description) description.textContent = configurationSummary;
    if (descriptionContainer) descriptionContainer.hidden = !configurationSummary;

    const available = item.dataset.clpAvailable === 'true';
    if (stockBadge) {
      stockBadge.textContent = available ? 'In stock' : 'Unavailable';
      stockBadge.classList.toggle('clp-stock-badge--available', available);
      stockBadge.classList.toggle('clp-stock-badge--unavailable', !available);
    }
  }

  function init(root) {
    if (!(root instanceof HTMLElement) || root.dataset.clpInitialized === 'true') return;
    root.dataset.clpInitialized = 'true';

    const items = [...root.querySelectorAll('[data-clp-config-item]')];
    const filtersContainer = root.querySelector('[data-clp-config-filters]');
    const search = root.querySelector('[data-clp-config-search]');
    const showSoldOut = root.querySelector('[data-clp-show-soldout]');
    const results = root.querySelector('[data-clp-config-results]');
    const chooser = root.querySelector('[data-clp-chooser]');

    if (!items.length) return;

    if (filtersContainer) {
      FILTERS.forEach((config) => {
        const filter = createFilter(root, items, config);
        if (filter) filtersContainer.append(filter);
      });
    }

    const applyFilters = () => {
      const query = clean(search?.value).toLowerCase();
      const activeFilters = [...root.querySelectorAll('[data-clp-filter-key]')]
        .filter((select) => select.value)
        .map((select) => [select.dataset.clpFilterKey, select.value]);
      const includeSoldOut = Boolean(showSoldOut?.checked);
      let visibleCount = 0;

      items.forEach((item) => {
        const input = item.querySelector('input[type="radio"]');
        const available = item.dataset.clpAvailable === 'true';
        const matchesQuery = !query || (item.dataset.clpSearch || '').includes(query);
        const matchesFilters = activeFilters.every(([key, value]) => clean(item.dataset[key]) === value);
        const keepSelectedVisible = Boolean(input?.checked);
        const visible = matchesQuery && matchesFilters && (includeSoldOut || available || keepSelectedVisible);

        item.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (results) {
        const suffix = includeSoldOut ? '' : ' currently available';
        results.textContent = `${visibleCount} configuration${visibleCount === 1 ? '' : 's'}${suffix}`;
      }
    };

    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-clp-config-search]')) applyFilters();
    });

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-clp-filter-key], [data-clp-show-soldout]')) {
        applyFilters();
        return;
      }

      if (event.target.matches('input[type="radio"]')) {
        const item = event.target.closest('[data-clp-config-item]');
        updateCurrent(root, item);
        [120, 400, 900].forEach((delay) => window.setTimeout(() => syncAll(), delay));
        window.setTimeout(() => {
          if (chooser) chooser.open = false;
        }, 80);
      }
    });

    chooser?.addEventListener('toggle', () => {
      if (chooser.open) window.setTimeout(() => search?.focus(), 60);
    });

    updateCurrent(root, items.find((item) => item.querySelector('input[type="radio"]:checked')));
    applyFilters();
  }

  function initAll(scope = document) {
    if (scope.matches?.('[data-clp-configurator]')) init(scope);
    scope.querySelectorAll?.('[data-clp-configurator]').forEach(init);
  }

  function syncAll(scope = document) {
    const roots = [];
    if (scope.matches?.('[data-clp-configurator]')) roots.push(scope);
    scope.querySelectorAll?.('[data-clp-configurator]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      const checked = root.querySelector('[data-clp-config-item] input[type="radio"]:checked');
      updateCurrent(root, checked?.closest('[data-clp-config-item]'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          initAll(node);
          window.setTimeout(() => syncAll(), 0);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
